import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import Card from 'shared/lib/components/Card';
import CardSectionHeading from '../CardSectionHeading';
import CardRowKeyVal from '../CardRowKeyVal';
import CardRowLink from '../CardRowLink';
import PolicyRow from '../PolicyRow';
import PolicyDetailsBaggageBRB from '../PolicyDetailsBaggageBRB';
import PolicyDetailsBaggageChubb from '../PolicyDetailsBaggageChubb';
import PolicyDetailsRentersAssurant from '../PolicyDetailsRentersAssurant';
import PolicyDetailsRentersQBE from '../PolicyDetailsRentersQBE';
import PolicyDetailsRentersNationwide from '../PolicyDetailsRentersNationwide';
import PolicyDetailsSmartphone from '../PolicyDetailsSmartphone';
import PolicyDetailsRentalCar from '../PolicyDetailsRentalCar';
import PolicyDetailsFlight from '../PolicyDetailsFlight';
import PolicyDetailsCollectibles from '../PolicyDetailsCollectibles';
import PolicyDetailsRides from '../PolicyDetailsRides';
import PolicyDetailsCommercial from '../PolicyDetailsCommercial';
import Spinner from 'shared/lib/components/Spinner';
import { Modal } from 'sure-components';
import VERTICALS from 'shared/lib/constants/verticals';
import PAYMENT_FREQUENCIES from '../../constants/paymentFrequencies';
import { Link } from 'react-router';
import capitalize from 'lodash/capitalize';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import moment from 'moment-timezone';
import * as analytics from 'shared/lib/analytics';
import { format as currencyFormat } from 'currency-formatter';
import './PolicyDetailsView.css';

const propTypes = {
  getPolicyById: PropTypes.func,
  params: PropTypes.shape({
    policyId: PropTypes.string,
    verticalCode: PropTypes.string,
  }).isRequired,
  policiesById: PropTypes.object,
  isFetchingPolicies: PropTypes.bool,
};

const defaultProps = {
  policiesById: {},
  isFetchingPolicies: false,
};

class PolicyDetailsView extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isPolicyDocsModalActive: false,
    };
  }

  componentDidMount() {
    this.props.getPolicyById(this.props.params.policyId).then(policy => {
      document.title = `${VERTICALS[policy.vertical_code].title} policy | Sure`;
    });
    analytics.page('Policy Management - Policy Detail');
  }

  handleCancel = () => {
    this.props.handleCancelPolicy(this.policy.id);
  };

  togglePolicyDocsModal = () => {
    this.setState({
      isPolicyDocsModalActive: !this.state.isPolicyDocsModalActive,
    });
  };

  trackClaimClick() {
    analytics.track('Policy Management - Submit a Claim Clicked');
  }

  trackDocsClick() {
    analytics.track('Policy Management - View Policy Docs Clicked');
  }

  get policy() {
    return this.props.policiesById[this.props.params.policyId];
  }

  get billingCycleNode() {
    const nodes = [];
    const paymentFrequency = get(this.policy, 'billing.payment_frequency');
    if (paymentFrequency) {
      const paymentFreqKey = get(this.policy, 'billing.payment_frequency');
      const paymentFreqConfig = PAYMENT_FREQUENCIES[paymentFreqKey];
      const paymentFreqLabel = get(paymentFreqConfig, 'billingCycleLabel');

      if (paymentFreqLabel) {
        nodes.push(
          <CardRowKeyVal
            key="billingCycle"
            title="Billing cycle"
            detail={capitalize(paymentFreqLabel)}
          />
        );
      }
    }

    const nextPayment = get(this.policy, 'billing.next_payment');
    if (nextPayment) {
      // technically we should only need the "new" date format
      // but just in case let's support this old format, too
      // new format example: 11-26-2023T00:00:00+00:00
      let theMoment = moment(nextPayment, 'YYYY-MM-DD');

      if (!theMoment.isValid()) {
        theMoment = moment(nextPayment, 'MM-DD-YYYY');
      }

      const formattedMoment = theMoment.format('MMM DD, YYYY');

      nodes.push(
        <CardRowKeyVal
          key="nextPayment"
          title="Next billing date"
          detail={formattedMoment}
        />
      );
    }

    return nodes;
  }

  get paymentMethodDescription() {
    switch (get(this.policy, 'billing.payment_method.type')) {
      case 'APPLE_PAY_STRIPE':
        return 'Apple Pay';
      case 'CREDIT_CARD_STRIPE':
        let { brand, last4 } = get(
          this.policy,
          'billing.payment_method.details'
        );
        return `${brand} •••• ${last4}`;
      default:
        return null;
    }
  }

  get verticalDetails() {
    switch (this.policy.format_type) {
      case 'brb_baggage':
        return <PolicyDetailsBaggageBRB {...this.policy} />;
      case 'baggage':
        return <PolicyDetailsBaggageChubb {...this.policy} />;
      case 'renters_assurant':
        return <PolicyDetailsRentersAssurant {...this.policy} />;
      case 'renters_qbe':
        return <PolicyDetailsRentersQBE {...this.policy} />;
      case 'renters_nationwide':
        return <PolicyDetailsRentersNationwide {...this.policy} />;
      case 'devices':
        return <PolicyDetailsSmartphone {...this.policy} />;
      case 'rental_car':
        return <PolicyDetailsRentalCar {...this.policy} />;
      case 'flights':
        return <PolicyDetailsFlight {...this.policy} />;
      case 'collectibles':
        return (
          <PolicyDetailsCollectibles
            {...this.policy}
            account={this.props.account}
            updateCollectiblesEndorsementAddress={
              this.props.updateCollectiblesEndorsementAddress
            }
            getEndorsementRemoveCheckout={
              this.props.getEndorsementRemoveCheckout
            }
            submitEndorsementRemove={this.props.submitEndorsementRemove}
            getPolicyById={this.props.getPolicyById}
            navTo={this.props.navTo}
            onError={this.props.onError}
          />
        );
      case 'chubb_taxi':
        return <PolicyDetailsRides {...this.policy} />;
      case 'commercial_chubb':
        return <PolicyDetailsCommercial {...this.policy} />;
      default:
        return null;
    }
  }

  get policyDocsActionNode() {
    if (isEmpty(this.policy.documents)) {
      return null;
    } else if (this.policy.documents.length > 1) {
      return (
        <CardRowLink onClick={this.togglePolicyDocsModal}>
          View policy docs
        </CardRowLink>
      );
    } else {
      return (
        <CardRowLink
          to={get(this.policy, 'documents[0].url')}
          onClick={this.trackDocsClick}
        >
          View policy docs
        </CardRowLink>
      );
    }
  }

  get verticalActionsNodes() {
    const actionNodes = [];
    switch (this.policy.format_type) {
      case 'rental_car':
        if (this.policy.details.extension_allowed) {
          actionNodes.push(
            <CardRowLink
              key="extendCoverage"
              onClick={this.props.onRentalCarExtend}
            >
              Extend coverage
            </CardRowLink>
          );
        }
        break;
      case 'collectibles':
        if (this.policy.status === 'approved') {
          actionNodes.push(
            <CardRowLink
              key="addToCollection"
              onClick={this.props.handleAddToCollection}
            >
              Add a new collection
            </CardRowLink>
          );
        }
        break;
      default:
        break;
    }
    return actionNodes;
  }

  get loadingView() {
    return (
      <div className="PolicyDetailsView-loader">
        <Spinner />
        <p>Fetching your policy...</p>
      </div>
    );
  }

  get priceDescription() {
    const { billing, vertical_code } = this.policy;
    switch (vertical_code) {
      case 'renters':
        if (get(billing, 'payment_frequency') === 'eleven_pay') {
          const monthlyPayment = find(billing.payment_schedule.schedule, {
            downpayment: false,
          });
          const downPayment = find(billing.payment_schedule.schedule, {
            downpayment: true,
          });

          const firstPayment = downPayment
            ? downPayment.amount
            : monthlyPayment.amount;

          return (
            <Fragment>
              <span style={{ fontSize: '14px' }}>
                First payment: {currencyFormat(firstPayment, { code: 'USD' })}
              </span>
              <br />
              <span style={{ fontSize: '14px' }}>
                Followed by:{' '}
                {currencyFormat(monthlyPayment.amount, { code: 'USD' })}
              </span>
            </Fragment>
          );
        } else {
          return get(billing, 'price_desc');
        }
      case 'pet':
        const petPriceStr = currencyFormat(
          get(this.policy, 'billing.payment_schedule.schedule[0].amount'),
          {
            code: 'USD',
          }
        );
        return `${petPriceStr}/month`;
      default:
        return get(this.policy, 'billing.price_desc');
    }
  }

  get lineItemTotal() {
    const { billing, vertical_code } = this.policy;
    switch (vertical_code) {
      case 'renters':
        if (billing.payment_frequency === 'eleven_pay') {
          const monthlyPayment = find(billing.payment_schedule.schedule, {
            downpayment: false,
          }).amount;
          const monthlyPriceStr = currencyFormat(monthlyPayment, {
            code: 'USD',
          });
          return `${monthlyPriceStr}/month`;
        } else {
          return get(this.policy, 'billing.price_desc');
        }
      case 'pet':
      case 'landlord':
        const priceStr = currencyFormat(
          get(this.policy, 'billing.payment_schedule.schedule[0].amount'),
          {
            code: 'USD',
          }
        );
        return `${priceStr}/month`;
      default:
        return get(this.policy, 'billing.price_desc');
    }
  }

  render() {
    return (
      <div className="PolicyDetailsView Container">
        <Link
          className="PolicyDetailsView-backLink"
          to={`/account/policies/${this.props.params.verticalCode}`}
        >
          <span className="material-icons">arrow_back</span> Back
        </Link>
        <Card>
          {this.props.isFetchingPolicies || !this.policy ? (
            this.loadingView
          ) : (
            <div>
              <PolicyRow
                key={this.policy.id}
                policyId={this.policy.id}
                icon={VERTICALS[this.policy.vertical_code].icon}
                createdAt={this.policy.created}
                verticalCode={this.policy.vertical_code}
                underwriter={this.policy.underwriter.name}
                details={this.policy.details}
                priceDesc={this.priceDescription}
                status={this.policy.status}
                withBadge
                isHeader
              />
              <CardRowKeyVal
                title="Underwritten by"
                detail={get(this.policy, 'underwriter.name')}
              />
              {this.policy.policy_number && (
                <CardRowKeyVal
                  title="Policy #"
                  detail={get(this.policy, 'policy_number')}
                />
              )}
              <CardRowKeyVal
                title="Purchased from"
                detail={get(this.policy, 'purchased_from')}
              />

              {this.verticalDetails}

              <CardSectionHeading>Billing Details</CardSectionHeading>
              {this.billingCycleNode}
              <CardRowKeyVal
                title="Payment method"
                detail={this.paymentMethodDescription}
              />
              {(get(this.policy, 'billing.price_desc') ||
                get(this.policy, 'vertical_code') === 'landlord') && (
                <CardRowKeyVal title="Total" detail={this.lineItemTotal} />
              )}
              <div>
                <CardSectionHeading />
                {this.verticalActionsNodes}
                {this.policyDocsActionNode}
                {get(this.policy, 'options.claimable') && (
                  <CardRowLink
                    to={`/account/claims/new/${
                      this.policy.vertical_code
                    }/policies/${this.policy.policy_number}`}
                    onClick={this.trackClaimClick}
                  >
                    Submit a claim
                  </CardRowLink>
                )}
                {get(this.policy, 'options.cancellable') && (
                  <CardRowLink type="destructive" onClick={this.handleCancel}>
                    Cancel policy
                  </CardRowLink>
                )}
              </div>
            </div>
          )}
        </Card>
        <Modal
          className="PolicyDetailsVew-policyDocsModal"
          title="Policy documents"
          isActive={this.state.isPolicyDocsModalActive}
          onDismiss={this.togglePolicyDocsModal}
        >
          {map(get(this.policy, 'documents'), (doc, i) => (
            <CardRowLink key={i} to={doc.url} onClick={this.trackDocsClick}>
              {doc.name}
            </CardRowLink>
          ))}
        </Modal>
      </div>
    );
  }
}

PolicyDetailsView.propTypes = propTypes;
PolicyDetailsView.defaultProps = defaultProps;

export default PolicyDetailsView;
